import has from 'lodash/has';
import omit from 'lodash/omit';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import findIndex from 'lodash/findIndex';
import {
  RESET_INCIDENT_DETAILS,
  ADD_RESOLVERS_SUCCESS,
  DISMISS_RESOLVER_SUCCESS,
  FETCH_COLLABORATIONS_SUCCESS,
  FETCH_INCIDENT_DETAILS_FAIL,
  FETCH_INCIDENT_DETAILS_START,
  FETCH_INCIDENT_DETAILS_SUCCESS,
  FETCH_RESOLVERS_SUCCESS,
  FETCH_RESOLVER_ROLES_SUCCESS,
  FETCH_ORDERED_RESOLVERS_AND_ROLES_SUCCESS,
  SAVE_RESOLVERS_ORDER_START,
  SAVE_RESOLVERS_ORDER_SUCCESS,
  SAVE_RESOLVERS_ORDER_FAIL,
  CHANGE_RESOLVERS_ORDER,
  SAVE_INCIDENT_DETAILS_START,
  SAVE_INCIDENT_DETAILS_FAIL,
  SAVE_INCIDENT_DETAILS_SUCCESS,
  SET_INCIDENT_OWNER_START,
  FETCH_INCIDENT_DETAILS_AUTO_FAIL,
  FETCH_INCIDENT_DETAILS_AUTO_START,
  FETCH_INCIDENT_DETAILS_AUTO_SUCCESS,
  STOP_NOTIFYING_RESOLVER_SUCCESS,
  STOP_NOTIFYING_RESOLVERS_SUCCESS,
  REFRESH_INCIDENT,
  CREATE_INCIDENT_ROLE_START,
  CREATE_INCIDENT_ROLE_SUCCESS,
  CREATE_INCIDENT_ROLE_FAIL,
  ADD_RESOLVER_TO_ROLE,
  DELETE_INCIDENT_ROLE_SUCCESS,
  REMOVE_RESOLVER_FROM_ROLE,
  RENAME_INCIDENT_ROLE_SUCCESS,
  RENAME_INCIDENT_ROLE_FAIL,
  CLEAR_INCIDENT_ROLE_ERROR,
  SET_INCIDENT_ROLES_ERROR,
  SET_MOCK_ROLE,
  MOVE_RESOLVER_WITHIN_ROLES,
  ASSOCIATE_PARTICIPANTS_WITH_BRIDGE,
  RESET_PARTICIPANTS_ON_BRIDGES,
  ADD_STAKEHOLDERS_SUCCESS,
  FETCH_STAKEHOLDERS_SUCCESS,
  DELETE_STAKEHOLDER_LOCALLY,
  NOTIFY_STAKEHOLDERS_FAIL,
  NOTIFY_STAKEHOLDERS_START,
  NOTIFY_STAKEHOLDERS_SUCCESS,
  SET_SELECTED_SERVICE_OWNER_RESOLVER,
  REMOVE_RESOLVER_SUCCESS,
} from 'actions/incidentManagement/incidentDetails';
import { formatRecipientName } from 'utils/recipientSearch';
import { getFilledRecipient } from 'utils/incidents';
import {
  INCIDENT_RESOLVER_STATE,
  DELETED_USER_TYPES,
} from 'xm-constants/incidentManagement';
import { sortByDisplayName } from 'pages/IntegrationManager/containers/Integrations/util/commonMapStateToProps';
import { getUniqueResolvers, transformIncident } from './utils';

const initialState = {
  collaborations: [],
  error: undefined, // Don't use null; undefined allows _.get to fill in the default values
  incident: undefined,
  loading: false,
  isResolversLoaded: false,
  refreshIncident: true,
  resolvers: [],
  roles: [],
  // The mock role that represents the one we are creating right now
  mockRole: null,
  // This contains the order of the resolvers and roles
  // Organized the same as the PUT request to update the order
  // i.e. [{ id: '...', type: 'RESOLVER' }, { id: '...', type: 'ROLE', resolvers: [{ id: '...', type: 'RESOLVER' }]}]
  resolversOrder: [],
  // If we have changed the order, then we don't want to update the order every tick
  resolversOrderDirty: false,
  rolesError: undefined,
  latestBridgeParticipants: {}, // Map of related bridge id's (in collaborations) to arrays of participant user id's currently on call
  stakeholders: [],
  stakeholderMessageLoading: false,
  selectedServiceOwnerResolverId: null, // the currently selected impacted service owner
};

/*
 * Move the value from fromIndex to toIndex
 * Note that the indexes are from before the operation,
 * so toIndex may not be exactly where the element ends up
 */
const moveValueToIndex = (arr, fromIndex, toIndex) => {
  const arrCopy = [...arr];
  const newIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
  arrCopy.splice(newIndex, 0, ...arrCopy.splice(fromIndex, 1));
  // Return a copy so that the redux state is updated
  return arrCopy;
};

/**
 * Add a value at a certain index, shifting everything else over
 */
const addValueAtIndex = (arr, value, index) => {
  return [...arr.slice(0, index), value, ...arr.slice(index, arr.length)];
};

const findById = (arr, id) => arr.find(x => x.id === id);

const resolversOrderPayloadToResolvers = payload => [
  ...Object.values(payload)
    .filter(r => r.resolverType === 'RESOLVER')
    .map(r => r.sortable),
  ...reduce(
    Object.values(payload).filter(r => r.resolverType === 'ROLE'),
    (resolvers, role) => [...resolvers, ...role.sortable.resolvers.data],
    [],
  ),
];

const resolversOrderPayloadToRoles = payload =>
  Object.values(payload)
    .filter(r => r.resolverType === 'ROLE')
    .map(r => ({
      ...r.sortable,
      resolvers: r.sortable.resolvers.data,
    }));
/**
 * Turn the payload returned by xm-api into the resolversOrder object in the redux state
 * Gets rid of pagination, resolver data, etc
 */
const resolversOrderPayloadToOrderMap = payload =>
  Object.values(payload).map(r =>
    r.resolverType === 'RESOLVER'
      ? {
          id: r.sortable.id,
          type: 'RESOLVER',
        }
      : {
          id: r.sortable.id,
          type: 'ROLE',
          resolvers: r.sortable.resolvers.data.map(res => ({
            id: res.id,
            type: 'RESOLVER',
          })),
        },
  );

// Stakeholders from the server doesn't have their displayNames yet.
// Since we need them to sort, we might as well do it in reducers instead of in connections.
function formatStakeholders(recipients = {}) {
  return Object.values(recipients).map(recipient => ({
    ...recipient,
    displayName: formatRecipientName({
      recipient: getFilledRecipient(recipient, DELETED_USER_TYPES.STAKEHOLDER),
    }),
  }));
}

export default function incidentDetails(state = initialState, action) {
  switch (action.type) {
    case FETCH_COLLABORATIONS_SUCCESS:
      return {
        ...state,
        error: undefined,
        loading: false,
        collaborations: action.payload.data,
      };
    case FETCH_INCIDENT_DETAILS_AUTO_SUCCESS:
    case FETCH_INCIDENT_DETAILS_SUCCESS:
    case SAVE_INCIDENT_DETAILS_SUCCESS:
      return {
        ...state,
        error: undefined,
        loading: false,
        incident: transformIncident(action.payload),
        serverSeverity: action.payload.severity,
        serverStatus: action.payload.status,
      };
    case SET_INCIDENT_OWNER_START:
      return {
        ...state,
        incident: {
          ...state.incident,
          ownerResolver: action.ownerResolver,
        },
        resolvers: state.resolvers.map(r =>
          r.id === action.ownerResolver.id ? state.incident.ownerResolver : r,
        ),
        resolversOrder: [
          { type: 'RESOLVER', id: state.incident.ownerResolver.id },
          ...state.resolversOrder
            .filter(r => r.id !== action.ownerResolver.id)
            .map(r =>
              r.type === 'ROLE'
                ? {
                    ...r,
                    resolvers: r.resolvers.filter(
                      res => res.id !== action.ownerResolver.id,
                    ),
                  }
                : r,
            ),
        ],
        resolversOrderDirty: true,
      };
    case FETCH_RESOLVERS_SUCCESS: {
      // TODO: Handle pagination

      /**
       * The JSON data is like this: {
       *   engagers: [], // empty if no engagers.
       *   event: {}, // its lastNotifiedAt is the time when a resolver is notified, unless it was marked as engaged in which case they won't have a lastNotifiedAt
       *   incident: {}
       *   resolver: {}, // people being notified. May or may not respond.
       *   role: {}
       * }
       */
      return {
        ...state,
        error: undefined,
        loading: false,
        resolvers: action.payload.data,
      };
    }
    case FETCH_RESOLVER_ROLES_SUCCESS:
      return {
        ...state,
        roles: action.payload.data.map(r => ({
          ...r,
          resolvers: r.resolvers.data,
        })),
      };
    case FETCH_ORDERED_RESOLVERS_AND_ROLES_SUCCESS:
      return {
        ...state,
        resolvers: getUniqueResolvers(action.payload),
        roles: resolversOrderPayloadToRoles(action.payload),
        resolversOrder: state.resolversOrderDirty
          ? state.resolversOrder
          : resolversOrderPayloadToOrderMap(action.payload),
        isResolversLoaded: true,
      };
    case SAVE_RESOLVERS_ORDER_SUCCESS:
      return {
        ...state,
        resolvers: resolversOrderPayloadToResolvers(action.payload),
        roles: resolversOrderPayloadToRoles(action.payload),
        resolversOrder: resolversOrderPayloadToOrderMap(action.payload),
        resolversOrderDirty: false,
      };
    case CHANGE_RESOLVERS_ORDER:
      return {
        ...state,
        resolversOrder: moveValueToIndex(
          state.resolversOrder,
          state.resolversOrder.findIndex(r => r.id === action.dragged),
          action.targetIndex,
        ),
        resolversOrderDirty: true,
      };
    case SAVE_RESOLVERS_ORDER_START:
      return {
        ...state,
        resolversOrderDirty: true,
      };
    case SAVE_RESOLVERS_ORDER_FAIL:
      return {
        ...state,
        resolversOrderDirty: false,
      };
    case FETCH_INCIDENT_DETAILS_START:
      return {
        ...initialState,
        error: {},
        loading: true,
      };
    case SAVE_INCIDENT_DETAILS_START:
    case FETCH_INCIDENT_DETAILS_AUTO_START: {
      return {
        ...state,
        error: {},
        loading: true,
      };
    }
    case RESET_INCIDENT_DETAILS:
      return {
        ...initialState,
      };
    case SAVE_INCIDENT_DETAILS_FAIL:
      return { ...state }; // Error handling tackled at component level catch
    case FETCH_INCIDENT_DETAILS_AUTO_FAIL:
    case FETCH_INCIDENT_DETAILS_FAIL:
      return {
        ...state, // If fails, we need to set the error for the page to redirect
        error: action.payload.json,
        loading: false,
      };

    case DISMISS_RESOLVER_SUCCESS:
      // We just clear the mockRole to avoid UI errors
      return {
        ...state,
        resolvers: state.resolvers.map(r =>
          r.id === action.payload.id
            ? {
                ...r,
                state: INCIDENT_RESOLVER_STATE.DISMISSED,
              }
            : r,
        ),
        mockRole: null,
      };
    case REFRESH_INCIDENT:
      return {
        ...state,
        refreshIncident: action.payload,
      };
    case CREATE_INCIDENT_ROLE_START:
      return {
        ...state,
        resolversOrderDirty: true,
      };
    case CREATE_INCIDENT_ROLE_SUCCESS:
      return {
        ...state,
        roles: [
          ...reject(state.roles, r => r.id === action.payload.id).map(role => ({
            ...role,
            resolvers: reject(role.resolvers, res =>
              findById(action.resolvers, res.id),
            ),
          })),
          {
            ...action.payload,
            resolvers: action.resolvers,
          },
        ],
        // We set the resolvers role field here
        // This is only really used if INCIDENT_RESOLVERS_ROLES is on, but INCIDENT_RESOLVERS_ORDER is off
        resolvers: state.resolvers.map(resolver =>
          findById(action.resolvers, resolver.id)
            ? { ...resolver, role: action.payload }
            : resolver,
        ),
        resolversOrder: addValueAtIndex(
          // Remove the resolver from any existing roles
          state.resolversOrder.map(r => {
            if (r.type === 'ROLE') {
              return {
                ...r,
                resolvers: reject(r.resolvers, res =>
                  findById(action.resolvers, res.id),
                ),
              };
            }
            return r;
          }),
          // Add a new role
          {
            type: 'ROLE',
            id: action.payload.id,
            resolvers: action.resolvers,
          },
          action.position,
          // Remove the resolvers after so that the position is not invalidated
        ).filter(
          res =>
            res.type !== 'RESOLVER' ||
            action.resolvers.findIndex(r => r.id === res.id) === -1,
        ),
        rolesError: undefined,
        mockRole: undefined,
      };
    case RENAME_INCIDENT_ROLE_FAIL:
    case CREATE_INCIDENT_ROLE_FAIL:
      return {
        ...state,
        rolesError: action.payload.json,
      };
    case ADD_RESOLVER_TO_ROLE:
      return {
        ...state,
        resolvers: state.resolvers.map(r =>
          r.id === action.resolverId
            ? { ...r, role: { id: action.roleId } }
            : r,
        ),
        roles: state.roles.map(r =>
          r.id === action.roleId
            ? {
                ...r,
                resolvers: addValueAtIndex(
                  r.resolvers,
                  { id: action.resolverId },
                  action.position,
                ),
              }
            : r,
        ),
        resolversOrder: state.resolversOrder
          .filter(r => r.id !== action.resolverId)
          .map(r => {
            if (r.id === action.roleId) {
              return {
                ...r,
                resolvers: addValueAtIndex(
                  r.resolvers,
                  { id: action.resolverId, type: 'RESOLVER' },
                  action.position,
                ),
              };
            }
            return r;
          }),
      };
    case DELETE_INCIDENT_ROLE_SUCCESS:
      return {
        ...state,
        roles: reject(state.roles, role => role.id === action.payload.id),
        resolvers: state.resolvers.map(r =>
          has(r, 'role') && r.role.id === action.payload.id
            ? omit(r, 'role')
            : r,
        ),
        resolversOrder: [
          ...state.resolversOrder.slice(
            0,
            findIndex(state.resolversOrder, r => r.id === action.payload.id),
          ),
          ...action.payload.resolvers.data.map(r => ({
            type: 'RESOLVER',
            id: r.id,
          })),
          ...state.resolversOrder.slice(
            findIndex(state.resolversOrder, r => r.id === action.payload.id) +
              1,
            state.resolversOrder.length,
          ),
        ],
      };
    case REMOVE_RESOLVER_FROM_ROLE:
      return {
        ...state,
        roles: state.roles.map(r => ({
          ...r,
          resolvers: r.resolvers.filter(res => res.id !== action.resolverid),
        })),
        resolvers: state.resolvers.map(r =>
          r.id === action.resolverId ? omit(r, 'role') : r,
        ),
        resolversOrder: addValueAtIndex(
          state.resolversOrder.map(r => {
            if (r.type === 'ROLE') {
              return {
                ...r,
                resolvers: reject(
                  r.resolvers,
                  res => res.id === action.resolverId,
                ),
              };
            }
            return r;
          }),
          { type: 'RESOLVER', id: action.resolverId },
          action.position,
        ),
      };
    case MOVE_RESOLVER_WITHIN_ROLES:
      return {
        ...state,
        roles: state.roles.map(r =>
          r.id === action.toRoleId
            ? {
                ...r,
                resolvers: findById(r.resolvers, action.resolverId)
                  ? moveValueToIndex(
                      r.resolvers,
                      findIndex(
                        r.resolvers,
                        res => res.id === action.resolverId,
                      ),
                      action.position,
                    )
                  : addValueAtIndex(
                      r.resolvers,
                      { id: action.resolverId },
                      action.position,
                    ),
              }
            : {
                ...r,
                resolvers: reject(
                  r.resolvers,
                  res => res.id === action.resolverId,
                ),
              },
        ),
        resolvers: state.resolvers.map(r =>
          r.id === action.resolverId
            ? {
                ...r,
                role: { id: action.toRoleId },
              }
            : r,
        ),
        resolversOrder: state.resolversOrder.map(r => {
          if (r.id === action.toRoleId) {
            // Check if we are moving the resolver within the same role
            if (findById(r.resolvers, action.resolverId)) {
              return {
                ...r,
                resolvers: moveValueToIndex(
                  r.resolvers,
                  findIndex(r.resolvers, res => res.id === action.resolverId),
                  action.position,
                ),
              };
            }
            return {
              ...r,
              resolvers: addValueAtIndex(
                r.resolvers,
                { id: action.resolverId },
                action.position,
              ),
            };
          }
          return {
            ...r,
            resolvers: reject(r.resolvers, res => res.id === action.resolverId),
          };
        }),
      };
    case RENAME_INCIDENT_ROLE_SUCCESS:
      return {
        ...state,
        roles: state.roles.map(role =>
          role.id === action.payload.id
            ? { ...role, name: action.payload.name }
            : role,
        ),
        rolesError: undefined,
      };
    case CLEAR_INCIDENT_ROLE_ERROR:
      return { ...state, rolesError: undefined };
    case SET_INCIDENT_ROLES_ERROR:
      return {
        ...state,
        rolesError: {
          subcode: action.error,
        },
      };
    case SET_MOCK_ROLE:
      return {
        ...state,
        mockRole: action.mockRole,
      };
    case RESET_PARTICIPANTS_ON_BRIDGES: {
      return {
        ...state,
        latestBridgeParticipants: {},
      };
    }
    case ASSOCIATE_PARTICIPANTS_WITH_BRIDGE: {
      const latestBridgeParticipants = {
        ...state.latestBridgeParticipants,
        [action.bridgeId]: action.participants,
      };
      return {
        ...state,
        latestBridgeParticipants,
      };
    }
    case ADD_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        // The server guarantees it won't add duplicated records, so we can assume the returned results are all new
        // However, since they only return the newly added ones, we'll have to sort them here on client
        stakeholders: [
          ...state.stakeholders,
          ...formatStakeholders(action.payload),
        ].sort(sortByDisplayName),
      };
    case FETCH_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        stakeholders: formatStakeholders(action.payload),
      };
    case DELETE_STAKEHOLDER_LOCALLY:
      return {
        ...state,
        stakeholders: state.stakeholders.filter(
          stakeholder => stakeholder.id !== action.payload.stakeholderId,
        ),
      };
    case NOTIFY_STAKEHOLDERS_START:
      return {
        ...state,
        stakeholderMessageLoading: true,
        error: undefined,
      };
    case NOTIFY_STAKEHOLDERS_FAIL:
      return {
        ...state,
        stakeholderMessageLoading: false,
        error: action.payload.json,
      };
    case NOTIFY_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        stakeholderMessageLoading: false,
        error: undefined,
      };

    case SET_SELECTED_SERVICE_OWNER_RESOLVER:
      return {
        ...state,
        selectedServiceOwnerResolverId: action.payload.resolverId,
      };
    case REMOVE_RESOLVER_SUCCESS:
      return {
        ...state,
        resolvers: state.resolvers.filter(r => r.id !== action.id),
      };

    case ADD_RESOLVERS_SUCCESS:
    case STOP_NOTIFYING_RESOLVER_SUCCESS:
    case STOP_NOTIFYING_RESOLVERS_SUCCESS:
    default:
      return state;
  }
}
